import React, { useState, useEffect, useRef } from 'react';
import { CardApple, ButtonApple, BadgeApple } from './apple-ui';
import { 
  MessageSquare, Send, CheckCircle, Clock, XCircle, 
  Gift, ChevronDown, ChevronUp, Loader2, Upload, X, Image
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEEDBACK_TYPES = [
  { value: 'bug_report', label: 'Bug Report', icon: '🐛' },
  { value: 'feature_suggestion', label: 'Feature Suggestion', icon: '💡' },
  { value: 'general', label: 'General Feedback', icon: '💬' }
];

const STATUS_CONFIG = {
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved — 1,000 words awarded', color: 'bg-green-100 text-green-700', icon: Gift },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const FeedbackSection = () => {
  const [showForm, setShowForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [totalRewards, setTotalRewards] = useState(0);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    feedback_type: 'general',
    subject: '',
    description: '',
    screenshot: null
  });

  useEffect(() => {
    if (showHistory) {
      loadFeedbackHistory();
    }
  }, [showHistory]);

  const loadFeedbackHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/feedback/my-feedback`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeedbackList(data.feedback || []);
        setTotalRewards(data.total_rewards_earned || 0);
      }
    } catch (error) {
      console.error('Failed to load feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Screenshot must be less than 2MB');
      return;
    }
    
    setFormData(prev => ({ ...prev, screenshot: file }));
  };

  const removeScreenshot = () => {
    setFormData(prev => ({ ...prev, screenshot: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    
    if (formData.description.trim().length < 50) {
      toast.error('Description must be at least 50 characters');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Use FormData for file upload support
      const submitData = new FormData();
      submitData.append('feedback_type', formData.feedback_type);
      submitData.append('subject', formData.subject.trim());
      submitData.append('description', formData.description.trim());
      if (formData.screenshot) {
        submitData.append('screenshot', formData.screenshot);
      }
      
      const response = await fetch(`${API_URL}/api/feedback/submit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: submitData
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || 'Feedback submitted!');
        setFormData({ feedback_type: 'general', subject: '', description: '', screenshot: null });
        if (fileInputRef.current) fileInputRef.current.value = '';
        setShowForm(false);
        // Refresh history if visible
        if (showHistory) loadFeedbackHistory();
      } else {
        toast.error(data.detail || 'Failed to submit feedback');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const charCount = formData.description.length;
  const isValidLength = charCount >= 50;

  return (
    <CardApple className="mt-6" data-testid="feedback-section">
      <CardApple.Header
        actions={
          <div className="flex gap-2">
            <ButtonApple 
              variant="secondary" 
              size="sm" 
              onClick={() => setShowHistory(!showHistory)}
              icon={showHistory ? ChevronUp : ChevronDown}
              data-testid="toggle-feedback-history"
            >
              History
            </ButtonApple>
            <ButtonApple 
              size="sm" 
              onClick={() => setShowForm(!showForm)}
              icon={showForm ? ChevronUp : MessageSquare}
              data-testid="toggle-feedback-form"
            >
              {showForm ? 'Close' : 'Give Feedback'}
            </ButtonApple>
          </div>
        }
      >
        <div className="flex items-center gap-3">
          <CardApple.Title>Feedback & Rewards</CardApple.Title>
          <BadgeApple variant="purple" size="sm">
            <Gift size={12} className="mr-1" />
            Earn 1,000 words
          </BadgeApple>
        </div>
      </CardApple.Header>

      <CardApple.Body>
        {/* Info Banner */}
        {!showForm && !showHistory && (
          <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-100">
            <div className="flex items-start gap-3">
              <Gift className="text-purple-600 mt-0.5" size={20} />
              <div>
                <p className="font-semibold text-purple-900">Help us improve & earn rewards!</p>
                <p className="text-sm text-purple-700">
                  Submit valuable feedback — bug reports, feature suggestions, or general comments. 
                  Approved feedback earns you <strong>1,000 bonus words</strong>!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Feedback Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="feedback-form">
            {/* Feedback Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Feedback Type *</label>
              <select
                value={formData.feedback_type}
                onChange={(e) => setFormData(prev => ({ ...prev, feedback_type: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                data-testid="feedback-type-select"
              >
                {FEEDBACK_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Brief title for your feedback"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                maxLength={100}
                required
                data-testid="feedback-subject-input"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your feedback in detail (minimum 50 characters)..."
                rows={5}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none ${
                  charCount > 0 && !isValidLength ? 'border-red-300' : 'border-gray-300'
                }`}
                maxLength={2000}
                required
                data-testid="feedback-description-input"
              />
              <div className="flex justify-between items-center mt-1">
                <p className={`text-xs ${charCount > 0 && !isValidLength ? 'text-red-500' : 'text-gray-400'}`}>
                  {charCount < 50 ? `${50 - charCount} more characters needed` : 'Minimum length reached'}
                </p>
                <p className="text-xs text-gray-400">{charCount}/2000</p>
              </div>
            </div>

            {/* Screenshot Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Screenshot <span className="text-gray-400 font-normal">(optional, max 2MB)</span>
              </label>
              
              {formData.screenshot ? (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <Image size={20} className="text-purple-600" />
                  <span className="text-sm text-gray-700 flex-1 truncate">{formData.screenshot.name}</span>
                  <span className="text-xs text-gray-500">
                    {(formData.screenshot.size / 1024).toFixed(1)} KB
                  </span>
                  <button
                    type="button"
                    onClick={removeScreenshot}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                  <Upload size={20} className="text-gray-400" />
                  <span className="text-sm text-gray-500">Click to upload screenshot</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleScreenshotChange}
                    className="hidden"
                    data-testid="feedback-screenshot-input"
                  />
                </label>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <ButtonApple 
                variant="secondary" 
                type="button" 
                onClick={() => setShowForm(false)}
              >
                Cancel
              </ButtonApple>
              <ButtonApple 
                type="submit" 
                disabled={submitting || !isValidLength || !formData.subject.trim()}
                icon={submitting ? Loader2 : Send}
                data-testid="submit-feedback-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </ButtonApple>
            </div>
          </form>
        )}

        {/* Feedback History */}
        {showHistory && (
          <div className="space-y-3" data-testid="feedback-history">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-purple-600" size={24} />
              </div>
            ) : feedbackList.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="mx-auto text-gray-300 mb-3" size={40} />
                <p className="text-gray-500">No feedback submitted yet</p>
                <ButtonApple 
                  variant="secondary" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => { setShowHistory(false); setShowForm(true); }}
                >
                  Submit your first feedback
                </ButtonApple>
              </div>
            ) : (
              <>
                {totalRewards > 0 && (
                  <div className="p-3 bg-green-50 rounded-xl border border-green-100 mb-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <Gift size={18} />
                      <span className="font-semibold">Total Rewards Earned: {totalRewards.toLocaleString()} words</span>
                    </div>
                  </div>
                )}
                
                {/* History Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Subject</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Reward</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {feedbackList.map((feedback) => {
                        const statusConfig = STATUS_CONFIG[feedback.status] || STATUS_CONFIG.pending_review;
                        const StatusIcon = statusConfig.icon;
                        const typeConfig = FEEDBACK_TYPES.find(t => t.value === feedback.feedback_type);
                        
                        return (
                          <tr key={feedback.id} className="hover:bg-gray-50" data-testid={`feedback-item-${feedback.id}`}>
                            <td className="px-4 py-3 text-gray-600">
                              {new Date(feedback.submitted_at || feedback.created_at).toLocaleDateString('en-IN', {
                                day: 'numeric', month: 'short', year: 'numeric'
                              })}
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1">
                                <span>{typeConfig?.icon || '💬'}</span>
                                <span className="text-gray-700">{typeConfig?.label || feedback.feedback_type}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-900 font-medium max-w-[200px] truncate">
                              {feedback.subject}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                                <StatusIcon size={12} />
                                {statusConfig.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {feedback.reward_granted && feedback.reward_words > 0 ? (
                                <span className="text-green-600 font-semibold">+{feedback.reward_words.toLocaleString()}</span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </CardApple.Body>
    </CardApple>
  );
};

export default FeedbackSection;
